import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Ask before a template is applied — every time, from every template picker.
 *
 * Dawn, 2026-10-05: one click on a template card replaced simongrayrealty.com's live pages, colours,
 * menu and hero photo. The pickers sent `confirm_replace: true` on the click itself, so nothing
 * ever asked. Now the click opens this, and only "Load into my draft" sends the confirmation.
 */
export function ConfirmTemplateDialog({
  templateName,
  open,
  onConfirm,
  onCancel,
}: {
  templateName: string | null;
  open: boolean;
  onConfirm: () => void;
  /** Runs on every close: Cancel, Escape, a click outside, and right after onConfirm. Keep it to
   *  clearing the picked template; never apply or undo anything here. */
  onCancel: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Try {templateName ?? 'this template'} on your site?</AlertDialogTitle>
          <AlertDialogDescription>
            Its pages, colors and fonts go into your draft so you can look them over in the Studio.
            If your site is already live, visitors keep seeing it as it is until you press Publish,
            and you can discard the template instead. Your other pages and your menu are kept.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Load into my draft</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** The toast after an apply: honest about whether anything went live. */
export function templateAppliedMessage(name: string, staged: boolean | undefined): string {
  return staged
    ? `${name} is in your draft. Your live site hasn't changed — press Publish when you're happy, or Discard it in the Studio.`
    : `${name} is applied. Customize it in the Studio.`;
}
